import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Euro, Play, Calendar, Loader2, ArrowRight } from 'lucide-react';

export default function MySeries() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadPurchases();
  }, [user, navigate]);

  const loadPurchases = async () => {
    try {
      const res = await api.get('/my-series');
      setPurchases(res.data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-ruby" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">As Minhas Séries</h1>
            <p className="text-muted-foreground">Séries que adquiriu</p>
          </div>
        </div>

        {purchases.length === 0 ? (
          <Card className="bg-surface border-border">
            <CardContent className="py-16 text-center">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">
                Ainda não adquiriu nenhuma série
              </h3>
              <p className="text-muted-foreground mb-6">
                Explora as nossas séries exclusivas e começa a ler histórias épicas.
              </p>
              <Button onClick={() => navigate('/series')} className="bg-ruby hover:bg-ruby-light">
                Explorar Séries
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <Card 
                key={purchase.id}
                className="bg-surface border-border cursor-pointer transition-all hover:border-ruby/50"
                onClick={() => navigate(`/series/${purchase.series_id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground text-lg">
                          {purchase.series_title}
                        </h3>
                        <Badge className="bg-green-500/20 text-green-400">
                          Adquirida
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(purchase.purchased_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Euro className="w-4 h-4" />
                          {purchase.amount_paid.toFixed(2)} pagos
                        </span>
                      </div>
                    </div>
                    <Button className="bg-ruby hover:bg-ruby-light">
                      <Play className="w-4 h-4 mr-2" />
                      Continuar a Ler
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {purchases.length > 0 && (
          <div className="mt-8 text-center">
            <Button variant="outline" onClick={() => navigate('/series')} className="border-border">
              Explorar Mais Séries
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
